import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, IconButton, CircularProgress, Dialog, TextField, alpha,
} from '@mui/material';
import {
  CloudUpload as UploadIcon, PhotoLibrary as GalleryIcon, Folder as FolderIcon,
  Close as CloseIcon, Check as CheckIcon, InsertDriveFile as FileIcon, ArrowBack as BackIcon,
} from '@mui/icons-material';
import axios, { API, getToken } from '../api';
import { designTokens as t } from '../theme';

const ACCENT = '#f43f5e';
const MAX_SLOTS = 20;

export default function MediaPicker({ open, onClose, onAdd, used = 0 }) {
  const [tab, setTab] = useState('upload');
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInput = useRef(null);

  // gallery
  const [folders, setFolders] = useState([]);
  const [galCurrent, setGalCurrent] = useState(null);
  const [galFiles, setGalFiles] = useState([]);
  const [galSel, setGalSel] = useState(new Set());
  const [galLoading, setGalLoading] = useState(false);

  // random
  const [randFolder, setRandFolder] = useState(null);
  const [randCount, setRandCount] = useState(1);

  const remaining = Math.max(0, MAX_SLOTS - used);

  const loadFolders = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/gallery/folders`);
      setFolders(data.folders || []);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    if (open) {
      setTab('upload'); setGalCurrent(null); setGalSel(new Set()); setRandFolder(null); setRandCount(1);
      loadFolders();
    }
  }, [open, loadFolders]);

  const openGalFolder = async (f) => {
    setGalCurrent(f); setGalSel(new Set()); setGalLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/gallery/files`, { params: { folder: f ? f.id : 0 } });
      setGalFiles(data.files || []);
    } catch (e) { setGalFiles([]); } finally { setGalLoading(false); }
  };

  const uploadFiles = async (files) => {
    const list = [...files].slice(0, remaining);
    if (!list.length) return;
    setUploading(true);
    const out = [];
    try {
      for (const file of list) {
        const fd = new FormData();
        fd.append('image', file);
        const { data } = await axios.post(`${API}/api/upload`, fd);
        out.push({ id: data.uploadId, preview: URL.createObjectURL(file) });
      }
      onAdd({ uploads: out });
      onClose();
    } catch (e) { /* ignore */ } finally { setUploading(false); }
  };

  const addGallerySelection = async () => {
    const ids = [...galSel];
    if (!ids.length) return;
    setUploading(true);
    const out = [];
    try {
      for (const id of ids) {
        const { data } = await axios.post(`${API}/api/gallery/files/${id}/attach`);
        out.push({ id: data.uploadId, preview: `${API}/api/gallery/files/${id}/raw?token=${getToken()}` });
      }
      onAdd({ uploads: out });
      onClose();
    } catch (e) { /* ignore */ } finally { setUploading(false); }
  };

  const confirmRandom = () => {
    if (!randFolder) return;
    onAdd({ random: { folder: randFolder.id, count: Number(randCount) || 1, name: randFolder.name } });
    onClose();
  };

  const Tab = ({ id, icon, label }) => (
    <Box role="button" onClick={() => setTab(id)}
      sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.85, borderRadius: 1.5, cursor: 'pointer', fontSize: '0.85rem', fontWeight: tab === id ? 700 : 500,
        bgcolor: tab === id ? alpha('#fff', 0.08) : 'transparent', color: tab === id ? t.foreground : t.muted, '&:hover': { bgcolor: alpha('#fff', 0.05) } }}>
      {icon}{label}
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { bgcolor: t.background, border: `1px solid ${t.border}`, borderRadius: 3, height: 600 } }}>
      {/* header tabs */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, p: 1.5, borderBottom: `1px solid ${t.border}` }}>
        <Tab id="upload" icon={<UploadIcon sx={{ fontSize: 18 }} />} label="Upload" />
        <Tab id="gallery" icon={<GalleryIcon sx={{ fontSize: 18 }} />} label="Gallery" />
        <Tab id="random" icon={<FolderIcon sx={{ fontSize: 18 }} />} label="Random" />
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={onClose}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        <input ref={fileInput} type="file" accept="image/*,video/*" hidden multiple onChange={(e) => { uploadFiles(e.target.files); e.target.value = ''; }} />

        {/* ===== UPLOAD ===== */}
        {tab === 'upload' && (
          <Box
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); uploadFiles(e.dataTransfer.files); }}
            onClick={() => fileInput.current && fileInput.current.click()}
            sx={{ height: '100%', minHeight: 380, border: `2px dashed ${dragOver ? ACCENT : t.border}`, borderRadius: 3, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', cursor: 'pointer', bgcolor: dragOver ? alpha(ACCENT, 0.05) : 'transparent', transition: 'all .15s' }}>
            {uploading ? <CircularProgress /> : (
              <>
                <Box sx={{ width: 64, height: 64, borderRadius: 2, bgcolor: alpha('#fff', 0.06), display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                  <FileIcon sx={{ fontSize: 30, color: t.muted }} />
                </Box>
                <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>Sélectionne des fichiers</Typography>
                <Typography sx={{ fontSize: '0.85rem', color: t.muted, mt: 0.5 }}>Glisse-dépose ou clique pour choisir</Typography>
                <Typography sx={{ fontSize: '0.78rem', color: t.mutedFaint, mt: 1 }}>JPEG, PNG, MP4, MOV</Typography>
              </>
            )}
          </Box>
        )}

        {/* ===== GALLERY ===== */}
        {tab === 'gallery' && (
          <Box>
            {galCurrent && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <IconButton size="small" onClick={() => { setGalCurrent(null); setGalSel(new Set()); }}><BackIcon sx={{ fontSize: 18 }} /></IconButton>
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>{galCurrent.name}</Typography>
              </Box>
            )}
            {!galCurrent ? (
              folders.length === 0 ? <Typography sx={{ color: t.mutedFaint, fontSize: '0.85rem' }}>Aucun dossier dans la Gallery.</Typography> : (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 1.5 }}>
                  {folders.map((f) => (
                    <Box key={f.id} onClick={() => openGalFolder(f)}
                      sx={{ border: `1px solid ${t.border}`, borderRadius: 2, p: 2, cursor: 'pointer', '&:hover': { borderColor: t.borderStrong } }}>
                      <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                        <FolderIcon sx={{ color: '#fff', fontSize: 22 }} />
                      </Box>
                      <Typography noWrap sx={{ fontSize: '0.88rem', fontWeight: 600 }}>{f.name}</Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: t.mutedFaint }}>{f.file_count} fichier(s)</Typography>
                    </Box>
                  ))}
                </Box>
              )
            ) : galLoading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box> : (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 1 }}>
                {galFiles.map((f) => {
                  const sel = galSel.has(f.id);
                  return (
                    <Box key={f.id} onClick={() => setGalSel((p) => { const n = new Set(p); n.has(f.id) ? n.delete(f.id) : n.add(f.id); return n; })}
                      sx={{ position: 'relative', aspectRatio: '1/1', borderRadius: 1.5, overflow: 'hidden', cursor: 'pointer', border: `2px solid ${sel ? ACCENT : 'transparent'}` }}>
                      <img src={`${API}/api/gallery/files/${f.id}/raw?token=${getToken()}`} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      {sel && <Box sx={{ position: 'absolute', top: 4, right: 4, width: 20, height: 20, borderRadius: '50%', bgcolor: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CheckIcon sx={{ fontSize: 13, color: '#fff' }} /></Box>}
                    </Box>
                  );
                })}
                {galFiles.length === 0 && <Typography sx={{ color: t.mutedFaint, fontSize: '0.85rem' }}>Dossier vide.</Typography>}
              </Box>
            )}
          </Box>
        )}

        {/* ===== RANDOM ===== */}
        {tab === 'random' && (
          <Box>
            <Typography sx={{ fontSize: '0.82rem', color: t.muted, mb: 1.5 }}>Choisis un dossier — des images seront tirées au hasard au moment de publier.</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {folders.map((f) => {
                const sel = randFolder && randFolder.id === f.id;
                return (
                  <Box key={f.id} onClick={() => setRandFolder(f)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1.25, borderRadius: 2, cursor: 'pointer',
                      bgcolor: sel ? alpha(ACCENT, 0.14) : t.card, border: `1px solid ${sel ? ACCENT : t.border}` }}>
                    <Box sx={{ width: 34, height: 34, borderRadius: 1.5, bgcolor: sel ? ACCENT : alpha('#fff', 0.08), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FolderIcon sx={{ color: sel ? '#fff' : t.muted, fontSize: 18 }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>{f.name}</Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: t.mutedFaint }}>{f.file_count} images</Typography>
                    </Box>
                    {sel && <CheckIcon sx={{ color: ACCENT }} />}
                  </Box>
                );
              })}
              {folders.length === 0 && <Typography sx={{ color: t.mutedFaint, fontSize: '0.85rem' }}>Aucun dossier dans la Gallery.</Typography>}
            </Box>
          </Box>
        )}
      </Box>

      {/* footer */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderTop: `1px solid ${t.border}` }}>
        {tab === 'random' ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '0.82rem', color: t.muted }}>Tirer</Typography>
            <TextField size="small" type="number" value={randCount} onChange={(e) => setRandCount(e.target.value)} inputProps={{ min: 1, max: 10 }} sx={{ width: 70 }} />
            <Typography sx={{ fontSize: '0.82rem', color: t.muted }}>image(s) au hasard</Typography>
          </Box>
        ) : (
          <Typography sx={{ fontSize: '0.8rem', color: t.mutedFaint }}>{remaining} emplacement(s) restant(s)</Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} sx={{ color: t.muted }}>Annuler</Button>
        {tab === 'upload' && (
          <Button variant="contained" onClick={() => fileInput.current && fileInput.current.click()} disabled={uploading} sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#e11d48' } }}>
            {uploading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Choisir des fichiers'}
          </Button>
        )}
        {tab === 'gallery' && (
          <Button variant="contained" onClick={addGallerySelection} disabled={galSel.size === 0 || uploading} sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#e11d48' } }}>
            {uploading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : `Ajouter (${galSel.size})`}
          </Button>
        )}
        {tab === 'random' && (
          <Button variant="contained" onClick={confirmRandom} disabled={!randFolder} sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#e11d48' } }}>Confirmer</Button>
        )}
      </Box>
    </Dialog>
  );
}
